/**
 * v2.3 FDT-O-004 — streak milestone seal (落款印章).
 *
 * Shown when the Daily streak hits 7 / 30 / 100 — the "why come back tomorrow"
 * beat (Royal-Match-Butler's-Gift philosophy, but no purchase, no nag). An
 * inline-SVG 篆刻-style seal (gold on ink), composed from the app's existing
 * inline-SVG / token vocabulary — no raster, no gradient, no glass (I-20). The
 * 100-day tier earns ONE restrained cinnabar accent (the brief's single
 * sanctioned red); 7 and 30 stay pure sacred gold. Auto-dismisses after 2.4s;
 * tapping anywhere also dismisses (R-004b). reduce-motion → no stamp animation.
 *
 * Reuses existing streak.* i18n (no new keys): milestone_title / _subtitle /
 * keep_going — all present in en/ja/zh-Hant/zh-Hans.
 */
import { t } from '../i18n/runtime';
import { el, html, on } from './html';

export const MILESTONES = [7, 30, 100] as const;
export type Milestone = (typeof MILESTONES)[number];

/** True only on the exact celebratory streak counts. */
export function isMilestone(streak: number): streak is Milestone {
  return (MILESTONES as readonly number[]).includes(streak);
}

/** I-20: gold for every tier; 100 alone breaks once into cinnabar (per B-3 brief). */
export function sealAccent(streak: number): 'gold' | 'cinnabar' {
  return streak >= 100 ? 'cinnabar' : 'gold';
}

/**
 * Build the milestone overlay element (caller appends to the screen root).
 * Self-manages dismissal; calls onDismiss after removal.
 */
export function milestoneSeal(
  streak: number,
  opts: { reduceMotion?: boolean; onDismiss?: () => void } = {},
): HTMLElement {
  const overlay = el(html`
    <div class="ms-overlay${opts.reduceMotion ? '' : ' ms-anim'}" role="dialog" aria-modal="true"
         aria-label="${t('streak.milestone_title')}" data-accent="${sealAccent(streak)}">
      <div class="ms-scrim" data-dismiss></div>
      <div class="ms-card" role="document">
        <div class="ms-title">${t('streak.milestone_title')}</div>
        <div class="ms-seal" aria-hidden="true">
          <svg width="128" height="128" viewBox="0 0 128 128" fill="none">
            <rect x="9" y="9" width="110" height="110" rx="16" fill="none" stroke="var(--ms-stroke)" stroke-width="3"/>
            <rect x="19" y="19" width="90" height="90" rx="10" fill="none" stroke="var(--ms-stroke)" stroke-width="1" opacity="0.5"/>
            <text x="64" y="82" text-anchor="middle" class="ms-num" fill="var(--ms-stroke)">${streak}</text>
          </svg>
        </div>
        <p class="ms-sub">${t('streak.milestone_subtitle', { n: streak })}</p>
        <button class="ms-cta" data-dismiss>${t('streak.keep_going')}</button>
      </div>
    </div>
  `);
  let done = false;
  const dismiss = (): void => {
    if (done) return;
    done = true;
    overlay.remove();
    opts.onDismiss?.();
  };
  on(overlay, 'click', '[data-dismiss]', dismiss);
  // R-004b — auto-dismiss so the overlay never blocks the flow.
  setTimeout(() => { if (overlay.isConnected) dismiss(); }, 2400);
  return overlay;
}
